/**
 * AI Gateway Service
 * 
 * This is the SINGLE entry point for ALL text generation across the ChefLab application.
 * All AI text generation requests MUST flow through this gateway, which handles:
 * 
 * 1. Reading model/provider configuration from modelService
 * 2. Routing to the appropriate provider (Gemini, Venice, or future providers)
 * 3. Consistent error handling and logging
 * 4. Rate limiting and retry logic (future)
 * 5. Usage tracking (future)
 * 
 * DO NOT call geminiApiService or veniceApiService directly for text generation.
 * Use this gateway instead.
 */

import { modelService } from '../modelService';

// ============================================================================
// TYPES
// ============================================================================

export type AIProvider = 'gemini' | 'venice' | 'openai' | 'anthropic';

export type AIFeature =
  | 'textGeneration'        // General text generation
  | 'assistantChat'         // Assistant conversations
  | 'recipeGeneration'      // Creating new recipes
  | 'recipeModification'    // Modifying existing recipes
  | 'studioGeneration'      // Studio recipe generation
  | 'recipeCleanup'         // Cleaning/formatting recipe text
  | 'intentDetection'       // Detecting user intent
  | 'imagePromptGeneration' // Generating prompts for image generation
  | 'dietaryAnalysis';      // Analyzing dietary information

export interface AIRequestOptions {
  /** The feature making this request — Venice models are feature-agnostic. */
  feature: AIFeature;
  /** Override the model selection (use sparingly) */
  modelOverride?: string;
  /** Temperature for generation (0-1) */
  temperature?: number;
  /** Maximum tokens to generate */
  maxTokens?: number;
  /** Thinking level (MINIMAL/LOW/MEDIUM/HIGH). Thinking is OFF by default. */
  thinkingLevel?: 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH';
  /**
   * Structured output format. Venice (the only provider) does not support
   * response_schema, so this is always dropped before dispatch — the caller
   * falls back to the markdown parsers.
   */
  responseFormat?: { mimeType: string; schema: unknown };
  /** Additional context for logging/debugging */
  context?: string;
}

export interface AIResponse {
  success: boolean;
  text?: string;
  error?: string;
  provider?: AIProvider;
  model?: string;
  latencyMs?: number;
  debugInfo?: any;
}

// ============================================================================
// PROVIDER IMPLEMENTATIONS (lazy loaded)
// ============================================================================

let veniceProvider: typeof import('../api/veniceApiService') | null = null;

async function getVeniceProvider() {
  if (!veniceProvider) {
    veniceProvider = await import('../api/veniceApiService');
  }
  return veniceProvider;
}

// ============================================================================
// MAIN GATEWAY FUNCTION
// ============================================================================

/**
 * Generate text using the configured AI provider.
 * This is the main entry point for ALL text generation in the application.
 * 
 * @param prompt The prompt to send to the AI
 * @param options Configuration options including feature type
 * @returns Promise resolving to AIResponse
 * 
 * @example
 * ```typescript
 * // Basic usage - uses configured model for the feature
 * const response = await generateText("Create a recipe for pasta", {
 *   feature: 'recipeGeneration'
 * });
 * 
 * // With model override (use sparingly)
 * const response = await generateText("Analyze this recipe", {
 *   feature: 'dietaryAnalysis',
 *   modelOverride: 'gemini-2.0-flash-lite'
 * });
 * ```
 */
export async function generateText(
  prompt: string,
  options: AIRequestOptions
): Promise<AIResponse> {
  const startTime = Date.now();

  try {
    // Venice is the only provider; all text features share the configured text
    // model (models/capabilities/pricing are dynamic from Venice — no name logic).
    const settings = await modelService.getSettings();

    const modelId = options.modelOverride || settings.textModel;
    const provider: AIProvider = 'venice';

    // Venice does not support response_schema — always drop responseFormat so
    // the caller falls back to the markdown parsers.
    const effectiveOptions: AIRequestOptions = { ...options, responseFormat: undefined };

    // eslint-disable-next-line no-console -- intentional diagnostic: routing path
    console.log(`aiGateway: [${options.feature}] routing to ${provider}/${modelId}`);
    const response: AIResponse = await callVenice(prompt, modelId, effectiveOptions);

    response.provider = provider;
    response.model = modelId;
    response.latencyMs = Date.now() - startTime;

    // eslint-disable-next-line no-console -- intentional diagnostic: latency/size on success
    if (response.success) console.log(`aiGateway: [${options.feature}] success (${response.latencyMs}ms, ${response.text?.length ?? 0} chars)`);
    // eslint-disable-next-line no-console -- intentional diagnostic: surfaced errors
    else console.error(`aiGateway: [${options.feature}] failed (${response.latencyMs}ms): ${response.error}`);

    return response;

  } catch (error) {
    const latencyMs = Date.now() - startTime;

    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error in AI gateway',
      latencyMs,
      debugInfo: error
    };
  }
}

// ============================================================================
// PROVIDER CALL IMPLEMENTATIONS
// ============================================================================

async function callVenice(
  prompt: string,
  modelId: string,
  options: AIRequestOptions
): Promise<AIResponse> {
  const venice = await getVeniceProvider();
  const result = await venice.generateVeniceContentInternal(prompt, modelId, {
    temperature: options.temperature,
    maxTokens: options.maxTokens,
    thinkingLevel: options.thinkingLevel,
  });

  return {
    success: result.success,
    text: result.text,
    error: result.error
  };
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Generate text for assistant chat
 */
export async function generateAssistantResponse(prompt: string, context?: string): Promise<AIResponse> {
  return generateText(prompt, { feature: 'assistantChat', context });
}

/**
 * Generate a new recipe
 * @param prompt The prompt to send
 * @param context Optional context string for logging
 * @param options Optional overrides — currently supports `responseFormat` for
 *                structured JSON output (used by paste import to skip markdown parsing).
 */
export async function generateRecipe(
  prompt: string,
  context?: string,
  options?: { responseFormat?: { mimeType: string; schema: unknown } }
): Promise<AIResponse> {
  return generateText(prompt, {
    feature: 'recipeGeneration',
    context,
    responseFormat: options?.responseFormat,
  });
}

/**
 * Generate recipe modification
 * @param prompt The prompt to send
 * @param context Optional context string for logging
 * @param options Optional overrides — supports `responseFormat` for structured JSON
 *                output (used by modification flows to skip markdown parsing).
 */
export async function generateRecipeModification(
  prompt: string,
  context?: string,
  options?: { responseFormat?: { mimeType: string; schema: unknown } }
): Promise<AIResponse> {
  return generateText(prompt, {
    feature: 'recipeModification',
    context,
    responseFormat: options?.responseFormat,
  });
}

/**
 * Generate content for studio
 * @param prompt The prompt to send
 * @param context Optional context string for logging
 * @param thinkingLevel Optional thinking level for Gemini 3 models
 * @param options Optional overrides — supports `responseFormat` for structured JSON
 *                output (used by the studio to skip markdown parsing).
 */
export async function generateStudioContent(
  prompt: string,
  context?: string,
  thinkingLevel?: 'MINIMAL' | 'LOW' | 'MEDIUM' | 'HIGH',
  options?: { responseFormat?: { mimeType: string; schema: unknown } }
): Promise<AIResponse> {
  return generateText(prompt, {
    feature: 'studioGeneration',
    context,
    thinkingLevel,
    responseFormat: options?.responseFormat,
  });
}

/**
 * Clean up/format recipe text
 */
export async function generateRecipeCleanup(prompt: string, context?: string): Promise<AIResponse> {
  return generateText(prompt, { feature: 'recipeCleanup', context });
}

/**
 * Detect user intent
 */
export async function generateIntentDetection(prompt: string, context?: string): Promise<AIResponse> {
  return generateText(prompt, { feature: 'intentDetection', context });
}

/**
 * Generate image prompt (for polishing/variation)
 * NOTE: This always uses a fast Gemini model regardless of settings,
 * as it's specifically for the image generation pipeline.
 */
export async function generateImagePrompt(prompt: string, context?: string): Promise<AIResponse> {
  return generateText(prompt, {
    feature: 'imagePromptGeneration',
    context,
  });
}

/**
 * Analyze dietary information
 */
export async function generateDietaryAnalysis(prompt: string, context?: string): Promise<AIResponse> {
  return generateText(prompt, { feature: 'dietaryAnalysis', context });
}

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/**
 * @deprecated Use generateText() with appropriate feature instead
 * Legacy function for backward compatibility during migration
 */
export async function legacyGenerateContent(
  prompt: string,
  modelId?: string
): Promise<AIResponse> {
  const response = await generateText(prompt, {
    feature: 'textGeneration',
    modelOverride: modelId,
    context: 'legacy-call'
  });

  return {
    success: response.success,
    text: response.text,
    error: response.error,
    debugInfo: response.debugInfo
  };
}
